import { Fragment, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { getReferralReportsPending } from "@/modules/indicators/services/indicator.api";
import { createPurchaseOrder } from "@/common/services/purchaseOrder.api";
import TableFooter from "@/common/components/ui/table/TableFooter";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import Loading from "@/common/components/ui/loading/Loading";
import {
  IndicatorPending,
  IndicatorsDataBank,
  PendingItem,
} from "@/types/Indicators.type";
import { useCompanyStore } from "@/common/states/store/Company";
import { formatDate } from "@/common/helpers/date.helpers";
import { maskCPF } from "@/common/helpers/mask.helpers";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { useUser } from "@/common/services/users.api";
import { getAllPurchaseRequestors } from "@/common/services/purchaseRequestor.api";
import { IPurchaseRequestor } from "@/types/purchaseRequestor.type";
import { getCode } from "@/lib/utils";
import {
  emptyPurchaseOrder,
  PURCHASEORDER_STATUS,
  TYPE_PAYMENT,
} from "@/types/purchaseOrder.types";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { usePurchaseOrderCheck } from "@/common/hooks";
import { formatISO } from "date-fns";

// eslint-disable-next-line react-refresh/only-export-components
export const STATUS_LABELS = {
  Recision: "Finalizado",
  Finished: "Recisão",
};

type OnCreatePurchaseOrderParams = {
  ind: {
    _id: string;
    name: string;
    cpf: string;
    companyId: string;
    dataBank: IndicatorsDataBank;
  };
  totalComissao: number;
  referrals: PendingItem[];
};

export default function ReferralReportsPage() {
  const { selectedCompanies, filterMode } = useCompanyStore();

  const [indicators, setIndicators] = useState<IndicatorPending[]>([]);
  const [referralsByIndicator, setReferralsByIndicator] = useState<
    Record<string, PendingItem[]>
  >({});
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-updatedAt",
  });
  const [requestors, setRequestors] = useState<IPurchaseRequestor[]>([]);

  const [page, setPage] = useState(1);
  const perPage = +(filters.itemsPerPage ?? 10);
  const [loadingCreatingOrder, setLoadingCreatingOrder] = useState({});

  const { notifySuccess, notifyError } = useToastContext();

  const CATEGORY_AMBASSADOR = {
    id: "dacbffac-f3aa-45e8-b36e-fb176e38b59d",
    name: "Embaixador",
  };

  const [selectedReferrals, setSelectedReferrals] = useState<
    Record<string, Record<string, boolean>>
  >({});

  const { check: checkPO } = usePurchaseOrderCheck();

  const COST_CENTER_COMMERCIAL = {
    id: "ad216570-4aac-41d5-ab22-1252182384da",
    name: "COMERCIAL",
  };

  useEffect(() => {
    async function loadRequestors() {
      const result = await getAllPurchaseRequestors();
      setRequestors(result.data);
    }

    loadRequestors();
  }, [selectedCompanies, filterMode, filters, page]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const startDate = filters.startDate
          ? formatISO(filters.startDate)
          : "";

        const endDate = filters.endDate
          ? formatISO(filters.endDate)
          : "";

        const result = await getReferralReportsPending({
          ...filters,
          startDate,
          endDate
        });

        const list: IndicatorPending[] = Array.isArray(result?.data)
          ? result.data
          : [];
        const map: Record<string, PendingItem[]> = {};
        for (const ind of list) {
          map[ind._id] = (ind.pendingItems ?? []).map((p) => ({
            name: p.name,
            cpf: p.cpf ?? "-",
            date: p.date,
            value: p.value ?? 100,
          }));
        }

        setIndicators(list);
        setReferralsByIndicator(map);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        console.error(e);
        setError("Não foi possível carregar os indicadores.");
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedCompanies, filterMode, filters, page]);

  const user = useUser();
  const filtered = useMemo(() => indicators, [indicators]);
  const totalItems = filtered.length;
  const pageStart = (page - 1) * perPage;
  const paginated = filtered.slice(pageStart, pageStart + perPage);
  const toggleRow = (indicatorId: string) => {
    setOpenRows((prev) => ({ ...prev, [indicatorId]: !prev[indicatorId] }));
  };

  const toggleReferralSelection = (
    indicatorId: string,
    referralIndex: number
  ) => {
    setSelectedReferrals((prev) => {
      const newSelection = { ...prev };
      if (!newSelection[indicatorId]) {
        newSelection[indicatorId] = {};
      }
      const indexKey = String(referralIndex);
      newSelection[indicatorId][indexKey] =
        !newSelection[indicatorId][indexKey];
      if (!newSelection[indicatorId][indexKey]) {
        delete newSelection[indicatorId][indexKey];
      }
      return newSelection;
    });
  };

  const toggleAllReferrals = (
    indicatorId: string,
    referrals: PendingItem[]
  ) => {
    setSelectedReferrals((prev) => {
      const newSelection = { ...prev };
      const currentSelection = newSelection[indicatorId] ?? {};
      const allSelected =
        referrals.length > 0 &&
        referrals.every((_, idx) => currentSelection[String(idx)]);

      if (allSelected) {
        newSelection[indicatorId] = {};
      } else {
        newSelection[indicatorId] = {};
        referrals.forEach((_, idx) => {
          newSelection[indicatorId][String(idx)] = true;
        });
      }
      return newSelection;
    });
  };

  const onCreatePurchaseOrder = async ({
    ind,
    totalComissao,
    referrals,
  }: OnCreatePurchaseOrderParams) => {
    setLoadingCreatingOrder((prev) => ({ ...prev, [ind._id]: true }));

    try {
      const existingPOs = await checkPO(ind._id);

      if (existingPOs.ok && existingPOs.data) {
        const newItemsDescriptions = referrals.map((r) => r.name).sort();
        if (newItemsDescriptions.length > 0) {
          for (const po of existingPOs.data) {
            const poItemsDescriptions = (po.itens || [])
              .map((item) => item.description)
              .sort();
            if (
              poItemsDescriptions.length === newItemsDescriptions.length &&
              JSON.stringify(newItemsDescriptions) ===
                JSON.stringify(poItemsDescriptions)
            ) {
              notifyError(
                "Já existe uma ordem de compra para este fornecedor com os mesmos itens."
              );
              return;
            }
          }
        }
      }

      const requestor = requestors.find(
        (req) => req.employeeEmail === user.email
      );
      const code = getCode();
      const defaultValues = emptyPurchaseOrder();
      const now = new Date();

      const normalizePixKey = (key: string) =>
        key
          .replace(/\(\)/g, "")
          .replace(/\./g, "")
          .replace(/\s/g, "")
          .replace(/-/g, "");

      const purchaseOrder = {
        ...defaultValues,
        isCounted: false,
        description: "Pagamento Embaixador",
        companyId: ind.companyId,
        requestorId: requestor?._id as string,
        requestorName: requestor?.name as string,
        requestorEmail: requestor?.employeeEmail as string,
        code: code.toString(),
        status: PURCHASEORDER_STATUS.PENDING,
        supplierId: ind._id,
        supplierName: ind.name,
        financial: {
          ...defaultValues.financial,
          categoryId: CATEGORY_AMBASSADOR.id,
          categoryName: CATEGORY_AMBASSADOR.name,
          costCenterId: COST_CENTER_COMMERCIAL.id,
          costCenterName: COST_CENTER_COMMERCIAL.name,
          typePayment: TYPE_PAYMENT.Pix,
          value: totalComissao,
          pixKey: normalizePixKey(ind?.dataBank?.chavePix),
          expectedDate: now,
          instalment: [
            {
              installmentNumber: "1",
              value: totalComissao,
              dueDate: now,
            },
          ],
        },
        itens: [
          ...referrals.map((ref: PendingItem) => ({
            description: ref.name,
            qtd: 1,
            value: ref.value,
          })),
        ],
        createdAt: now,
      };

      const result = await createPurchaseOrder(purchaseOrder, true);

      if (result.ok) {
        notifySuccess("Ordem de compra gerada com sucesso");
      } else {
        notifyError(
          result.message || `Erro ao gerar ordem de compra para ${ind.name}`
        );
      }
    } finally {
      setLoadingCreatingOrder((prev) => ({ ...prev, [ind._id]: false }));
    }
  };

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-destructive/30">
          <CardHeader>
            <CardTitle className="text-destructive">Erro</CardTitle>
          </CardHeader>
          <CardContent>{error}</CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <TableFilters
        dashboardHeader="Relatorio de indicações"
        placeholder="Digite o nome, o e-mail, o CPF ou a CNH."
        statusOptions={STATUS_LABELS}
        onNewItemClick={() => {}}
        onSearch={(data) => setFilters(data)}
        entity="indicators"
        tableData={referralsByIndicator as unknown as Record<string, unknown>[]}
      />

      {/* ✅ Mostrar loading OU conteúdo da tabela */}
      {isLoading ? (
        <Loading />
      ) : !paginated.length ? (
        <EmptyState />
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 text-left">
                  <th className="p-3">Nome</th>
                  <th className="p-3">CPF</th>
                  <th className="p-3">Total</th>
                  <th className="p-3"></th>
                  <th className="p-3 w-0 text-right">Indicações</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((ind) => {
                  const open = !!openRows[ind._id!];
                  const referrals = referralsByIndicator[ind._id!] ?? [];
                  const totalComissao = referrals.length * 100;
                  const selectedIdxs = Object.keys(
                    selectedReferrals[ind._id!] ?? {}
                  ).map(Number);
                  const selectedRefs = selectedIdxs.map(
                    (idx) => referrals[idx]
                  );
                  const selectedTotalComissao = selectedRefs.length * 100;

                  return (
                    <Fragment key={ind._id}>
                      <tr className="border-b">
                        <td className="p-3 align-top">
                          <div className="font-medium">{ind.name}</div>
                          <div className="text-xs text-muted-foreground">
                            Criado em {formatDate(ind.createdAt)}
                          </div>
                        </td>
                        <td className="p-3 align-top">{maskCPF(ind.cpf)}</td>
                        <td className="p-3 align-top">
                          R$ {totalComissao.toLocaleString("pt-BR")}
                        </td>
                        <td className="p-3 align-top w-[100px]">
                          <div className="flex items-center justify-end gap-2">
                            <ButtonWithLoader
                              isLoading={loadingCreatingOrder[ind._id]}
                              disabled={selectedRefs.length === 0}
                              onClick={() =>
                                onCreatePurchaseOrder({
                                  ind,
                                  totalComissao: selectedTotalComissao,
                                  referrals: selectedRefs,
                                })
                              }
                              className="!text-[14px] h-6 button-with-loader disabled:bg-redPrimary/70 w-[200px]"
                            >
                              Gerar ordem de compra
                            </ButtonWithLoader>
                          </div>
                        </td>
                        <td className="p-3 align-top">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => toggleRow(ind._id!)}
                              className="flex items-center gap-2"
                            >
                              <Users className="h-4 w-4" />
                              {open ? "Esconder indicados" : "Ver indicados"}
                              <span className="ml-1 text-xs text-muted-foreground">
                                ({referrals.length})
                              </span>
                              {open ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>

                      {open && (
                        <tr className="bg-muted/20 bg-warmGray-200">
                          <td colSpan={5} className="p-0">
                            <div className="px-4 py-3">
                              {referrals.length === 0 ? (
                                <div className="py-6 text-center text-muted-foreground">
                                  Nenhum indicado encontrado.
                                </div>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="text-left">
                                        <th className="p-2 w-10">
                                          <Checkbox
                                            className="border-gray-300 data-[state=checked]:text-red-500 data-[state=checked]:bg-transparent data-[state=checked]:border-red-500"
                                            checked={
                                              referrals.length > 0 &&
                                              referrals.every(
                                                (_, idx) =>
                                                  selectedReferrals[ind._id!]?.[
                                                    String(idx)
                                                  ]
                                              )
                                            }
                                            onCheckedChange={() =>
                                              toggleAllReferrals(
                                                ind._id!,
                                                referrals
                                              )
                                            }
                                          />
                                        </th>
                                        <th className="p-2">Nome</th>
                                        <th className="p-2">Data</th>
                                        <th className="p-2">Comissão</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {referrals.map((r, idx) => (
                                        <tr
                                          key={`${ind._id}-${idx}`}
                                          className="border-t"
                                        >
                                          <td className="p-2">
                                            <Checkbox
                                              className="border-gray-300 data-[state=checked]:text-red-500 data-[state=checked]:bg-transparent data-[state=checked]:border-red-500"
                                              checked={
                                                selectedReferrals[ind._id!]?.[
                                                  String(idx)
                                                ] ?? false
                                              }
                                              onCheckedChange={() =>
                                                toggleReferralSelection(
                                                  ind._id!,
                                                  idx
                                                )
                                              }
                                            />
                                          </td>
                                          <td className="p-2">{r.name}</td>
                                          <td className="p-2">
                                            {formatDate(r.date ?? "")}
                                          </td>
                                          <td className="p-2">R$ 100</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <TableFooter
        page={page}
        totalItems={totalItems}
        itemsPerPage={perPage}
        goToPage={setPage}
      />
    </div>
  );
}

function EmptyState() {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <div className="mx-auto mb-2 h-10 w-10 rounded-full border flex items-center justify-center">
          <Users className="h-5 w-5" />
        </div>
        <p className="text-muted-foreground">Nenhum indicador encontrado.</p>
      </CardContent>
    </Card>
  );
}
